import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { GrowthPaidMediaApi } from './growth-paid-media.api';
import {
  ApprovePaidMediaCampaignLinkRequest,
  CreatePaidMediaAccountRequest,
  CreatePaidMediaCampaignLinkRequest,
  GrowthPaidMediaAccountDto,
  GrowthPaidMediaAudienceSyncDto,
  GrowthPaidMediaCampaignLinkDto,
  GrowthPaidMediaRoasDto,
  GrowthPaidMediaSpendSnapshotDto,
  SetPaidMediaKillSwitchRequest,
  SubmitPaidMediaCampaignLinkRequest,
  SyncPaidMediaAudienceRequest,
  accountDisplayLabel,
  isIranResidency
} from './growth-paid-media.models';

@Injectable({ providedIn: 'root' })
export class GrowthPaidMediaService {
  private readonly api = inject(GrowthPaidMediaApi);

  listAccounts(): Observable<GrowthPaidMediaAccountDto[]> {
    return this.api.listAccounts();
  }

  getAccount(accountId: string): Observable<GrowthPaidMediaAccountDto | null> {
    return this.api.listAccounts().pipe(
      map(items => items.find(item => item.id.toLowerCase() === accountId.toLowerCase()) ?? null)
    );
  }

  createAccount(payload: CreatePaidMediaAccountRequest): Observable<GrowthPaidMediaAccountDto> {
    return this.api.createAccount(payload);
  }

  disableAccount(accountId: string): Observable<GrowthPaidMediaAccountDto> {
    return this.api.disableAccount(accountId);
  }

  setKillSwitch(accountId: string, payload: SetPaidMediaKillSwitchRequest): Observable<GrowthPaidMediaAccountDto> {
    return this.api.setKillSwitch(accountId, payload);
  }

  listCampaignLinks(accountId?: string | null): Observable<GrowthPaidMediaCampaignLinkDto[]> {
    return this.api.listCampaignLinks(accountId);
  }

  getCampaignLink(linkId: string, accountId?: string | null): Observable<GrowthPaidMediaCampaignLinkDto | null> {
    return this.api.listCampaignLinks(accountId).pipe(
      map(items => items.find(item => item.id.toLowerCase() === linkId.toLowerCase()) ?? null)
    );
  }

  createCampaignLink(payload: CreatePaidMediaCampaignLinkRequest): Observable<GrowthPaidMediaCampaignLinkDto> {
    return this.api.createCampaignLink(payload);
  }

  submitCampaignLink(linkId: string, payload: SubmitPaidMediaCampaignLinkRequest): Observable<GrowthPaidMediaCampaignLinkDto> {
    return this.api.submitCampaignLink(linkId, payload);
  }

  approveCampaignLink(linkId: string, payload?: ApprovePaidMediaCampaignLinkRequest | null): Observable<GrowthPaidMediaCampaignLinkDto> {
    return this.api.approveCampaignLink(linkId, payload);
  }

  syncAudience(payload: SyncPaidMediaAudienceRequest): Observable<GrowthPaidMediaAudienceSyncDto> {
    return this.api.syncAudience(payload);
  }

  pullSpend(campaignLinkId: string, periodStartUtc: string, periodEndUtc: string): Observable<GrowthPaidMediaSpendSnapshotDto[]> {
    return this.api.getSpend(campaignLinkId, periodStartUtc, periodEndUtc);
  }

  getRoas(campaignLinkId: string, periodStartUtc: string, periodEndUtc: string): Observable<GrowthPaidMediaRoasDto> {
    return this.api.getRoas(campaignLinkId, periodStartUtc, periodEndUtc);
  }

  accountOptions(accounts: GrowthPaidMediaAccountDto[]): Array<{ label: string; value: string }> {
    return accounts.map(item => ({ value: item.id, label: accountDisplayLabel(item) }));
  }

  residencyDisclosureKey(account: GrowthPaidMediaAccountDto | null | undefined): string | null {
    if (!account) {
      return null;
    }
    if (!account.canBeEnabled && account.providerKey === 'google-ads' && isIranResidency(account.nationCode)) {
      return 'growth.paidMedia.residency.googleAdsIranBlocked';
    }
    if (!account.canBeEnabled) {
      return 'growth.paidMedia.residency.cannotEnable';
    }
    if (account.killSwitchEnabled) {
      return 'growth.paidMedia.residency.killSwitchActive';
    }
    return null;
  }
}
